import { Layer, Source } from 'react-map-gl/mapbox'
import type { FeatureCollection } from 'geojson'
import type { createRoutes } from '../../util'
import {
  getFilter,
  getArrivalDepartureLayout,
  getArrowLayout,
  getCirclePaint,
  getLinePaint,
  getActionPinsLayout,
} from './constants'
import { message } from 'antd'
import type { MapboxMapHook } from '@/components/_SiteWide/MapboxMap/hooks/useMapboxMap'
import { useDriverTrackingState } from '@/components/OrderDispatch/DriverTracking/stores'
import type { GeoJSONFeature } from 'mapbox-gl'
import { useCallback } from 'react'

type OrderRouteLayerProps = {
  id: string
  breadcrumbRoute: ReturnType<typeof createRoutes>[string] | null
  actionPins: FeatureCollection
  mapboxMapHook: MapboxMapHook
  showBreadcrumbs: boolean | undefined
}

export function OrderRouteLayer({
  breadcrumbRoute,
  actionPins,
  id,
  mapboxMapHook,
  showBreadcrumbs = false,
}: OrderRouteLayerProps) {
  const { showLayerPopover, hideLayerPopover, useLayer, isStyleLoaded } = mapboxMapHook
  const { driverOrdersOnRoute } = useDriverTrackingState()

  // NOTE: temp disabled until we can figure out rerendering issue
  const handleCopyLatLon = useCallback((feature: GeoJSONFeature | null) => {
    if (!feature || !feature.geometry) return

    // Type guard to check if geometry has coordinates
    if ('coordinates' in feature.geometry) {
      const coordinates = feature.geometry.coordinates
      if (coordinates && coordinates.length >= 2) {
        const [lon, lat] = coordinates
        navigator.clipboard.writeText(`[${lat}, ${lon}]`)
        message.success(`Coordinates copied: [${lat}, ${lon}]`)
      }
    }
  }, [])

  useLayer(`route-points::${id}`, {
    onMouseEnter: showLayerPopover,
    onMouseLeave: hideLayerPopover,
    // onClick: handleCopyLatLon,
  })
  useLayer(`action-markers::${id}`, { onMouseEnter: showLayerPopover, onMouseLeave: hideLayerPopover })
  useLayer(`arrival-points::${id}`, { onMouseEnter: showLayerPopover, onMouseLeave: hideLayerPopover })
  useLayer(`departure-points::${id}`, { onMouseEnter: showLayerPopover, onMouseLeave: hideLayerPopover })

  const routeProperties = driverOrdersOnRoute[parseInt(id)]
  const visibility = routeProperties?.isVisible ? 'visible' : 'none'
  const breadcrumbsExist = breadcrumbRoute && routeProperties

  if (!isStyleLoaded) return null

  return (
    <>
      {breadcrumbsExist && (
        <>
          <Source key={`route-${id}`} id={`route-${id}`} type='geojson' data={breadcrumbRoute?.line}>
            <Layer
              id={`route-line::${id}`}
              type='line'
              paint={getLinePaint(routeProperties.color)}
              layout={{ visibility }}
            />
            <Layer
              id={`route-arrows::${id}`}
              type='symbol'
              layout={getArrowLayout(routeProperties.arrow, visibility)}
            />
          </Source>
          {showBreadcrumbs && (
            <Source key={`points-${id}`} id={`points-${id}`} type='geojson' data={breadcrumbRoute?.points}>
              <Layer
                id={`route-points::${id}`}
                type='circle'
                paint={getCirclePaint(driverOrdersOnRoute[parseInt(id)]?.color)}
                layout={{ visibility }}
              />
            </Source>
          )}
        </>
      )}
      {actionPins && (
        <Source key={`loading-dropping-pins-${id}`} id={`loading-dropping-pins-${id}`} type='geojson' data={actionPins}>
          <Layer
            id={`arrival-points::${id}`}
            type='symbol'
            layout={getArrivalDepartureLayout('arrival', visibility)}
            filter={getFilter('event', 'arrival')}
          />
          <Layer
            id={`departure-points::${id}`}
            type='symbol'
            layout={getArrivalDepartureLayout('departure', visibility)}
            filter={getFilter('event', 'departure')}
          />
          <Layer
            id={`action-markers::${id}`}
            type='symbol'
            layout={getActionPinsLayout(visibility)}
            filter={getFilter('popoverType', 'load-drop')}
          />
        </Source>
      )}
    </>
  )
}
